import { useQuery } from '@tanstack/react-query'
import { controlCenterApi, type Host } from '@/lib/api'
import StatusBadge from '@/components/StatusBadge'
import PageHeader from '@/components/PageHeader'

type ServersResponse = {
  success?: boolean
  servers?: Host[]
  source?: string
  configured?: boolean
  note?: string
}

export default function Servers() {
  const { data, isLoading, isError } = useQuery<ServersResponse>({
    queryKey: ['control-center', 'servers'],
    queryFn: controlCenterApi.listServers,
  })

  const hosts = data?.servers ?? []
  const reachable = hosts.filter((h) => h.health.reachable).length

  return (
    <div className="p-8 space-y-6">
      <PageHeader
        title="Servers"
        description={
          data?.configured
            ? `${reachable} of ${hosts.length} hosts reachable. Source: ${data.source}`
            : 'Host inventory configuration not found.'
        }
      />

      {isError ? (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          Failed to load host inventory.
        </div>
      ) : null}

      {data?.note ? (
        <div className="rounded-md border border-amber-500/30 bg-amber-500/5 px-4 py-3 text-sm text-amber-400">
          {data.note}
        </div>
      ) : null}

      <div className="rounded-lg border border-border bg-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/40">
            <tr className="text-left">
              <th className="px-4 py-2.5 font-medium">Host</th>
              <th className="px-4 py-2.5 font-medium">Provider</th>
              <th className="px-4 py-2.5 font-medium">Address</th>
              <th className="px-4 py-2.5 font-medium">Role</th>
              <th className="px-4 py-2.5 font-medium">Specs</th>
              <th className="px-4 py-2.5 font-medium">Health</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-muted-foreground">
                  Probing hosts…
                </td>
              </tr>
            ) : hosts.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-muted-foreground">
                  No hosts declared in <code className="font-mono">hosts.yaml</code>.
                </td>
              </tr>
            ) : (
              hosts.map((h) => (
                <tr key={h.id} className="border-t border-border">
                  <td className="px-4 py-2.5">
                    <div className="font-medium">{h.name}</div>
                    <div className="text-xs text-muted-foreground font-mono">{h.id}</div>
                  </td>
                  <td className="px-4 py-2.5 text-muted-foreground">
                    {h.provider} · {h.region}
                  </td>
                  <td className="px-4 py-2.5 font-mono text-xs">{h.ip}</td>
                  <td className="px-4 py-2.5 text-xs">{h.role}</td>
                  <td className="px-4 py-2.5 text-xs text-muted-foreground">
                    {h.cpu_cores > 0 ? `${h.cpu_cores}c · ${h.memory_gb}gb · ${h.disk_gb}gb` : '—'}
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex flex-col gap-1">
                      <StatusBadge
                        status={h.health.reachable ? 'reachable' : 'unreachable'}
                        tone={h.health.reachable ? 'success' : 'danger'}
                      />
                      {h.health.probe !== 'none' ? (
                        <div className="text-[10px] text-muted-foreground">
                          {h.health.probe}:{h.health.port}
                          {typeof h.health.durationMs === 'number' ? ` · ${h.health.durationMs}ms` : ''}
                          {h.health.error ? ` · ${h.health.error}` : ''}
                        </div>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
